import { createClient } from "@supabase/supabase-js";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ZipmapRepository from "../../repository/ZipmapRepository";
import DashboardSideNav from "./DashboardSideNav";
import EditButton from "../../shared/components/dashboard/EditButton";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CoorayFooter from "../../shared/components/footer/CoorayFooter";
import CurationListItem from "../../shared/components/curationitem/CurationListItem";
import DeleteButton from "../../shared/components/dashboard/DeleteButton";
import DashboardEditPlaceModal from "./DashboardEditPlaceModal";
import GalleryCarouselPage from "../gallery/GalleryCarouselPage";
import CarouselModal from "../../shared/components/carousel/CarouselModal";
import LocationPickerModal from "../locationpicker/LocationPickerModal";
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from "../../shared/components/sortable/SortableItem";

export default function DashboardPageEditPlaces({ analytics, viewingMap }) {

    const navigate = useNavigate()
    const [loadingText, setLoadingText] = useState(null)
    const [places, setPlaces] = useState([])
    const [editingPlace, setEditingPlace] = useState(null)
    const [isEditPlaceModalOpen, setIsEditPlaceModalOpen] = useState(false)
    const [bigCarouselPage, setBigCarouselPage] = useState(null)
    const [carouselImages, setCarouselImages] = useState([])
    const [toastMessage, setToastMessage] = useState(null)
    const [deletingPlace, setDeletingPlace] = useState(null)
    const [fullScreenLoadingText, setFullScreenLoadingText] = useState(null)
    const [isSorting, setIsSorting] = useState(false)

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
          coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {

        async function getZipmapDetails(slug) {
            setLoadingText("Loading ...")
            const zipmapRepository = new ZipmapRepository()
            const zipmapResponse = await zipmapRepository.getZipmapDetails(slug, "recommended")
            console.log(zipmapResponse)
      
            setPlaces(
                zipmapResponse.places.map((place) => {
                    return {
                      ...place,
                      title: place.name,
                    }
                })
            )
            setLoadingText(null)
          }

          getZipmapDetails(viewingMap.slug)
    }, [viewingMap])

    useEffect(() => {
        if (toastMessage) {
            setTimeout(() => {
                setToastMessage(null)
            }, 3000)
        }
    }, [toastMessage])


  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setPlaces((places) => {
        const oldIndex = places.findIndex((place) => place.id === active.id);
        const newIndex = places.findIndex((place) => place.id === over.id);
        return arrayMove(places, oldIndex, newIndex);
      });
    }
  }

    function onEditPlaceClick(place) {
        setEditingPlace(place)
        setIsEditPlaceModalOpen(true)
    }

    function onPlaceUpdated(place) {
        // setEditingPlace(place)
        var isExistingPlace = false
        const updatedPlaces = places.map((p) => {
            if (p.id === place.id) {
                isExistingPlace = true
                return {
                    ...place,
                    title: place.name,
                }
            }
            return p
        })
        if (!isExistingPlace) {
            updatedPlaces.push(
                {
                    ...place,
                    title: place.name,
                }
            )
            setToastMessage({
                message: `🎉 ${place.name} added successfully`,
                type: "success"
            })
        } else {
            setToastMessage({
                message: `🎉 ${place.name} updated successfully`,
                type: "success"
            })
        }
        setPlaces(updatedPlaces)
        setEditingPlace(null)
        setIsEditPlaceModalOpen(false)
    }

    function onEditPlaceModalDismiss() {
        setEditingPlace(null)
        setIsEditPlaceModalOpen(false)
    }

    function onDeleteTitleClick() {
    }

    function onCarouselModalDismiss() {
        setBigCarouselPage(null)
        setCarouselImages([])
    }

    function onEditPlaceImageClick(images, index) {
        setCarouselImages(images)
        setBigCarouselPage(index)
    }

    function onAddPlaceClick() {
        setEditingPlace(null)
        setIsEditPlaceModalOpen(true)
    }

    async function confirmDeletePlace() {
        document.getElementById("delete-place-confirmation-modal").close()
        setFullScreenLoadingText("Deleting ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.deletePlace(viewingMap.slug, deletingPlace.id)
        setPlaces(places.filter((p) => p.id !== deletingPlace.id))
        setToastMessage({
            message: `✔ ${deletingPlace.name} deleted successfully`,
            type: "info"
        })
        setDeletingPlace(null)
        setFullScreenLoadingText(null)
    }

    function onDeletePlaceClick(place) {
        setDeletingPlace(place)
        document.getElementById("delete-place-confirmation-modal").showModal()
    }

    async function confirmUpdatePlacesSequence() {
        setFullScreenLoadingText("Updating ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.updateMapPlacesSequence(viewingMap.slug, places.map((p) => p.id))
        setToastMessage({
            message: `✔ Places sequence updated successfully`,
            type: "info"
        })
        setFullScreenLoadingText(null)
        setIsSorting(false)
    }

    function onUpdatePlacesSequenceClick() {
        if (isSorting) {
            confirmUpdatePlacesSequence()
        } else {
            setIsSorting(true)
        }
    }

    return (
        <div className="h-full flex flex-col flex-1 py-4 bg-base-200">
            {
                loadingText && <div className="flex justify-center items-center h-full flex flex-row gap-2">
                    <span className="loading loading-spinner text-primary"></span>
                    {loadingText}
                </div>
            }
            {
                !loadingText && places.length == 0 && (
                    <div className="flex justify-center items-center h-full flex flex-row gap-2">
                        <span className="text-base-content">🤷🏻‍♀️ No places in this map yet</span>
                    </div>
                )
            }
            {
                !loadingText && places.length > 0 && (
                    <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    >
                    <SortableContext
                        items={places.map((place) => place.id)}
                        strategy={verticalListSortingStrategy}
                    >
                    {
                        places.map((place, index) => {
                            return <SortableItem key={place.id} id={place.id} isDisabled={!isSorting}>
                                <div className="flex flex-row gap-2 items-center">
                                    {
                                        isSorting && (
                                            <div className="shrink-0 h-full flex flex-col items-center gap-2 justify-center pl-2 animate-bounce">
                                                <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <g>
                                                        <g>
                                                        <path className="stroke-base-content" d="M18 14C17.4477 14 17 14.4477 17 15C17 15.5523 17.4477 16 18 16C18.5523 16 19 15.5523 19 15C19 14.4477 18.5523 14 18 14Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path className="stroke-base-content" d="M12 14C11.4477 14 11 14.4477 11 15C11 15.5523 11.4477 16 12 16C12.5523 16 13 15.5523 13 15C13 14.4477 12.5523 14 12 14Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path className="stroke-base-content" d="M6 14C5.44772 14 5 14.4477 5 15C5 15.5523 5.44772 16 6 16C6.55228 16 7 15.5523 7 15C7 14.4477 6.55228 14 6 14Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path className="stroke-base-content" d="M18 8C17.4477 8 17 8.44772 17 9C17 9.55228 17.4477 10 18 10C18.5523 10 19 9.55228 19 9C19 8.44772 18.5523 8 18 8Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path className="stroke-base-content" d="M12 8C11.4477 8 11 8.44772 11 9C11 9.55228 11.4477 10 12 10C12.5523 10 13 9.55228 13 9C13 8.44772 12.5523 8 12 8Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path className="stroke-base-content" d="M6 8C5.44772 8 5 8.44772 5 9C5 9.55228 5.44772 10 6 10C6.55228 10 7 9.55228 7 9C7 8.44772 6.55228 8 6 8Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </g>
                                                    </g>
                                                </svg>
                                            </div>
                                        )
                                    }
                                    <div className="flex-1">
                                        <CurationListItem 
                                            clickEvent={(_) => {
                                            //    handleItemClick(place.id, place)
                                            }} 
                                            placeItem={place} 
                                            key={place.key}
                                            hoverInEvent={() => {
                                            //   setCurationHoverId(place.id)
                                            }}
                                            hoverOutEvent={() => {
                                            //   setCurationHoverId(null)
                                            }}
                                            mapEmoji={place.emoji ?? viewingMap.defaultMarker}
                                        />
                                    </div>
                                    <div className="shrink-0 h-full flex flex-col items-center gap-2 justify-center pr-4">
                                        <EditButton onEditClick={() => {onEditPlaceClick(place)}} />
                                        <DeleteButton onDeleteClick={() => {onDeletePlaceClick(place)}} />
                                    </div>
                                </div>
                            </SortableItem>
                        })
                    }
                    </SortableContext>
                    </DndContext>
                )
              }

              
            <div className='max-w-1/2 fixed bottom-4 right-4 bg-transparent text-right flex flex-row gap-2 items-center'>
            <button className={`btn text-base btn-secondary normal-case rounded-full ${isSorting ? "btn-success" : "btn-secondary"}`} onClick={() => { onUpdatePlacesSequenceClick() }}>
            {
                !isSorting && <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path className="stroke-secondary-content" d="M16 18L16 16M16 6L20 10.125M16 6L12 10.125M16 6L16 13" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path className="stroke-secondary-content" d="M8 18L12 13.875M8 18L4 13.875M8 18L8 11M8 6V8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg> 
            }
            {isSorting ? "Done" : "Sort"}
            </button>

            <button className="btn text-base btn-primary normal-case rounded-full" onClick={() => { onAddPlaceClick() }}>
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path className="fill-primary-content" fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12.75 9C12.75 8.58579 12.4142 8.25 12 8.25C11.5858 8.25 11.25 8.58579 11.25 9L11.25 11.25H9C8.58579 11.25 8.25 11.5858 8.25 12C8.25 12.4142 8.58579 12.75 9 12.75H11.25V15C11.25 15.4142 11.5858 15.75 12 15.75C12.4142 15.75 12.75 15.4142 12.75 15L12.75 12.75H15C15.4142 12.75 15.75 12.4142 15.75 12C15.75 11.5858 15.4142 11.25 15 11.25H12.75V9Z"/>
                    </svg> Add Place
            </button>

            <a href={`/${viewingMap.slug}`} target="_blank">
                <button className="btn text-base btn-accent normal-case rounded-full" onClick={() => {  }}>
                <svg className="w-6 h-6 fill-accent-content" viewBox="0 0 52 52" enable-background="new 0 0 52 52" >
                <g>
                    <path d="M51.8,25.1C47.1,15.6,37.3,9,26,9S4.9,15.6,0.2,25.1c-0.3,0.6-0.3,1.3,0,1.8C4.9,36.4,14.7,43,26,43
                        s21.1-6.6,25.8-16.1C52.1,26.3,52.1,25.7,51.8,25.1z M26,37c-6.1,0-11-4.9-11-11s4.9-11,11-11s11,4.9,11,11S32.1,37,26,37z"/>
                    <path d="M26,19c-3.9,0-7,3.1-7,7s3.1,7,7,7s7-3.1,7-7S29.9,19,26,19z"/>
                </g>
                </svg> Preview Map
                </button>
            </a>

            </div>
            <div className="pb-64"></div>
            <CoorayFooter background='bg-base-200' />

            {
                isEditPlaceModalOpen && <DashboardEditPlaceModal
                isEditPlaceModalOpen={isEditPlaceModalOpen}
                onPlaceUpdated={onPlaceUpdated}
                onEditPlaceModalDismiss={onEditPlaceModalDismiss}
                initialPlace={editingPlace}
                mapDetails={viewingMap}
                onImageClick={onEditPlaceImageClick}
                />
            }
            {
                    fullScreenLoadingText &&
                    <div className='w-full h-screen absolute max-w-[100%] top-0 flex flex-row items-center justify-center gap-2 backdrop-blur-sm bg-base-100/30 z-50'>
                        <div className="loading loading-infinity loading-lg"></div>
                        <div className='font-normal text-base-content'>{fullScreenLoadingText}</div>
                    </div>
            }
            {
                bigCarouselPage != null && <CarouselModal isCarouselModalOpen={bigCarouselPage != null} onCarouselModalDismiss={onCarouselModalDismiss} images={carouselImages} currentIndex={bigCarouselPage}/>
            }
            {
                toastMessage && <div className="toast z-10">
                <div className={`alert ${toastMessage.type == "success" ? "alert-success" : "alert-info"}`}>
                <span className="text-sm">{toastMessage.message}</span>
                </div>
            </div>
            }
            {
                <dialog id="delete-place-confirmation-modal" className="modal">
                    <div className="modal-box w-11/12 max-w-5xl">
                        <p className="py-2 text-base">Are you sure you want to delete this place?</p>
                        <div className="modal-action">
                            <button className="btn btn-ghost" onClick={() => { document.getElementById("delete-place-confirmation-modal").close()}}>Cancel</button>
                            <button className="btn btn-error" onClick={() => { confirmDeletePlace() }}>Delete</button>
                        </div>
                    </div>
                </dialog>
            }

        </div>
    )
}
